import { useState, useEffect } from 'react';
import { X, Search, Building2, MapPin, Star, Mail, Phone } from 'lucide-react';
import { useBusinesses } from '../../service-providers/hooks/useBusinesses';

interface AssignBusinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (businessId: string) => Promise<void>;
  postalCode?: string;
}

/**
 * Modal for assigning a quote to a specific business.
 * Displays a list of active businesses, optionally filtered by Postal code.
 * Allows admin to search and select a business to assign the quote to.
 *
 * @component
 * @param {AssignBusinessModalProps} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {() => void} props.onClose - Callback when modal is closed
 * @param {(businessId: string) => Promise<void>} props.onAssign - Callback when a business is assigned
 * @param {string} [props.postalCode] - Optional Postal code to filter businesses by service area
 */
export default function AssignBusinessModal({
  isOpen,
  onClose,
  onAssign,
  postalCode
}: Readonly<AssignBusinessModalProps>) {
  const { businesses: allBusinesses, loading: loadingAll, getBusinessesByPostalCode } = useBusinesses();
  const [postalBusinesses, setPostalBusinesses] = useState<typeof allBusinesses>([]);
  const [loadingPostal, setLoadingPostal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);

  // Extract numeric Postal code from strings like "TX 76208" or "76208"
  const normalizePostalCode = (postal: string | undefined): string | null => {
    if (!postal) return null;
    // Extract 5-digit Postal code using regex
    const regex = /\b\d{5}\b/;
    const match = regex.exec(postal);
    return match ? match[0] : null;
  };

  const normalizedPostalCode = normalizePostalCode(postalCode);

  // Fetch businesses by Postal code when modal opens (uses database index!)
  useEffect(() => {
    if (isOpen && normalizedPostalCode && getBusinessesByPostalCode) {
      setLoadingPostal(true);
      getBusinessesByPostalCode(normalizedPostalCode)
        .then(setPostalBusinesses)
        .catch(err => {
          console.error('Failed to fetch businesses by Postal:', err);
          setPostalBusinesses([]);
        })
        .finally(() => setLoadingPostal(false));
    } else {
      setPostalBusinesses([]);
    }
  }, [isOpen, normalizedPostalCode, getBusinessesByPostalCode]);

  // Use Postal-filtered businesses if available, otherwise all businesses
  const availableBusinesses = normalizedPostalCode ? postalBusinesses : allBusinesses;
  const loading = normalizedPostalCode ? loadingPostal : loadingAll;

  // Get appropriate empty state message
  const getEmptyStateMessage = () => {
    if (searchTerm) return 'No businesses match your search';
    if (normalizedPostalCode) return `No active businesses service Postal code ${normalizedPostalCode}`;
    return 'No active businesses available';
  };

  // Filter businesses: active status and search term
  // Postal filtering is already done at the database level via getBusinessesByPostalCode
  const filteredBusinesses = availableBusinesses.filter(business => {
    if (business.status !== 'active') return false;

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        business.businessName.toLowerCase().includes(search) ||
        business.ownerName.toLowerCase().includes(search) ||
        business.email.toLowerCase().includes(search)
      );
    }

    return true;
  });

  const handleAssign = async () => {
    if (!selectedBusinessId) return;

    try {
      console.log('Assigning business ID:', selectedBusinessId);
      setAssigning(true);
      await onAssign(selectedBusinessId);
      onClose();
    } catch (error) {
      console.error('Failed to assign business:', error);
      alert('Failed to assign business. Please try again.');
    } finally {
      console.log('Finished assigning');
      setAssigning(false);
    }
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSelectedBusinessId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <Building2 className="h-6 w-6 mr-3" />
              Assign to Service Provider
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
              disabled={assigning}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          {normalizedPostalCode && (
            <p className="text-indigo-100 text-sm mt-2 flex items-center">
              <MapPin className="h-4 w-4 mr-1" />
              Showing service providers servicing Postal code: {normalizedPostalCode}
            </p>
          )}
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by business name, owner, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={assigning}
            />
          </div>
        </div>

        {/* Business List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading service providers...</div>
            </div>
          ) : null}
          
          {!loading && filteredBusinesses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Building2 className="h-12 w-12 text-gray-300 mb-3" />
              <p className="text-gray-500 text-center">
                {getEmptyStateMessage()}
              </p>
            </div>
          ) : null}
          
          {!loading && filteredBusinesses.length > 0 ? (
            <div className="space-y-3">
              {filteredBusinesses.map((business) => (
                <button
                  key={business.id}
                  onClick={() => setSelectedBusinessId(business.id)}
                  disabled={assigning}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    selectedBusinessId === business.id
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                  } ${assigning ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-1">
                        <Building2 className="h-5 w-5 text-indigo-600 mr-2" />
                        <h3 className="font-semibold text-gray-900">{business.businessName}</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{business.ownerName}</p>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Mail className="h-4 w-4 mr-1" />
                          {business.email}
                        </span>
                        <span className="flex items-center">
                          <Phone className="h-4 w-4 mr-1" />
                          {business.phone}
                        </span>
                      </div>
                      {business.servicePostalCodes && business.servicePostalCodes.length > 0 && (
                        <div className="mt-2 flex items-center text-xs text-gray-500">
                          <MapPin className="h-3 w-3 mr-1" />
                          Services: {business.servicePostalCodes.slice(0, 5).join(', ')}
                          {business.servicePostalCodes.length > 5 && ` +${business.servicePostalCodes.length - 5} more`}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end ml-4">
                      <div className="flex items-center text-yellow-500 mb-1">
                        <Star className="h-4 w-4 fill-current mr-1" />
                        <span className="font-semibold">{business.rating.toFixed(1)}</span>
                      </div>
                      <span className="text-xs text-gray-500">{business.completedJobs} jobs</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={assigning}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedBusinessId || assigning}
            className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-md"
          >
            {assigning ? 'Assigning...' : 'Assign Service Provider'}
          </button>
        </div>
      </div>
    </div>
  );
}
